import React, { createContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

export const PermissionContext = createContext(null);

/**
 * Key used for the default (production) environment in `environmentRoles`.
 * Must match PRODUCTION_ROLE_KEY in the backend's AccessService: production is
 * the absence of an environment stamp everywhere else, so it needs one agreed
 * constant to be addressable as a permission target.
 */
const PRODUCTION_ROLE_KEY = 'production';

const ROLE_HIERARCHY = { admin: 3, editor: 2, viewer: 1 };

export const PermissionProvider = ({ children }) => {
    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);
    // A role is now held PER ENVIRONMENT, so the answer changes when the user
    // switches. Someone can be admin of Staging and viewer of Production.
    const activeEnvironment = useSelector((state) => state.environments?.active);

    const membership = useMemo(() => {
        const details = currentWorkspace?.data?.user_details;
        if (!details || !currentUser?._id) return null;
        return details.find((user) => user._id === currentUser._id)?.role ?? null;
    }, [currentWorkspace, currentUser?._id]);

    const environmentKey = useMemo(
        () =>
            !activeEnvironment || activeEnvironment.isDefault
                ? PRODUCTION_ROLE_KEY
                : String(activeEnvironment._id),
        [activeEnvironment],
    );

    // The override for this environment wins; otherwise the workspace-wide
    // role, which is what every membership carried before per-environment
    // access existed — so nothing changes until a role is actually set.
    const userRole = useMemo(() => {
        if (!membership) return 'viewer';
        const override = membership.environmentRoles?.[environmentKey];
        if (override && ROLE_HIERARCHY[override]) return override;
        return membership.role || 'viewer';
    }, [membership, environmentKey]);

    // The workspace-wide role, kept separate because a few screens are about
    // the workspace itself (members, API keys) rather than its content.
    const workspaceRole = membership?.role || 'viewer';

    const loading = !currentWorkspace?.data;

    const hasPermission = (requiredRole) =>
        (ROLE_HIERARCHY[userRole] || 1) >= (ROLE_HIERARCHY[requiredRole] || 1);

    const hasWorkspacePermission = (requiredRole) =>
        (ROLE_HIERARCHY[workspaceRole] || 1) >= (ROLE_HIERARCHY[requiredRole] || 1);

    return (
        <PermissionContext.Provider value={{
            userRole,
            workspaceRole,
            environmentKey,
            hasPermission,
            hasWorkspacePermission,
            isAdmin: userRole === 'admin',
            isEditor: userRole === 'editor' || userRole === 'admin',
            isViewer: true,
            loading
        }}>
            {children}
        </PermissionContext.Provider>
    );
};
